import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import actions from "redux-form/lib/actions";
import formValueSelector from "redux-form/lib/formValueSelector";
import getIn from "redux-form/lib/structure/plain/getIn";
import ReactModal from "react-modal";
import Immutable from "immutable";
import { translate } from "react-i18next";
import { createSelector } from "reselect";

import SchemaField from "../../../component/SchemaField";
import { VK_ESCAPE } from "../../../lib/constants";
import Loader from "../../../component/Loader";
import { formClearSyncError } from "../../../store/modules/cart";

import "./Modal.scss";

const noop = s => s;

const customStyles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(255, 255, 255, 0.75)",
    zIndex: 9123456
  },
  content: {
    padding: 0,
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    zIndex: 91234567
  }
};

/**
 * DiscountCodeField
 */
class DiscountCodeField extends Component {
  static propTypes = {
    className: PropTypes.string,
    discountCode: PropTypes.string,
    name: PropTypes.string.isRequired,
    schema: PropTypes.instanceOf(Immutable.Map).isRequired,
    t: PropTypes.func,
    formBlur: PropTypes.func,
    formChange: PropTypes.func,
    formClearAsyncError: PropTypes.func,
    formClearSyncError: PropTypes.func,
    isValid: PropTypes.bool,
    isChecking: PropTypes.bool,
    fieldError: PropTypes.string
  };

  static contextTypes = {
    asyncValidate: PropTypes.func
  };

  static defaultProps = {
    className: null,
    discountCode: null,
    t: noop,
    formBlur: noop,
    formChange: noop,
    formClearAsyncError: noop,
    formClearSyncError: noop,
    isValid: false,
    isChecking: false,
    fieldError: null
  };

  constructor() {
    super();
    this.inputRef = React.createRef();
    this.modalContentRef = React.createRef();
  }

  state = {
    isOpen: false
  };

  componentWillMount() {
    // on document so escape keydown works.. it won't work on window
    document.addEventListener("keydown", this.onKeyDown);
  }

  componentWillReceiveProps(props) {
    if (this.props.isChecking && !props.isChecking && props.discountCode) {
      this.props.formBlur("cart", this.props.name, props.discountCode, true);
      if (this.context && this.context.asyncValidate) {
        this.context.asyncValidate();
      }
    }
  }

  componentWillUnmount() {
    document.removeEventListener("keydown", this.onKeyDown);
  }

  onKeyDown = e => {
    if (e.keyCode === VK_ESCAPE) {
      this.onModalCancel(e);
    }
  };

  onClick = () => {
    this.setState({ isOpen: true });
  };

  onModalCancel = () => {
    this.setState({ isOpen: false });
    this.props.formChange("cart", this.props.name, "");
    this.props.formBlur("cart", this.props.name, "");
    this.props.formClearAsyncError("cart", this.props.name);
    this.props.formClearSyncError("cart", this.props.name);
  };

  onModalAccept = () => {
    this.setState({ isOpen: false });
  };

  onAfterOpenModal = () => {
    if (!this.modalContentRef.current || !this.modalContentRef.current.getElementsByTagName) {
      return;
    }
    const els = this.modalContentRef.current.getElementsByTagName("input");
    if (!els || !els.length) {
      return;
    }
    els[0].focus();
  };

  render() {
    return (
      <div className={[this.props.className, "mb-3", this.props.fieldError && "has-danger"].filter(v => !!v).join(" ")}>
        <button onClick={this.onClick} type="button" className="btn btn-primary">
          {this.props.t("Received a discount coupon? Click here.")}
        </button>
        {this.props.fieldError && <div className="invalid-feedback">{this.props.fieldError}</div>}
        <ReactModal
          style={customStyles}
          ariaHideApp={false}
          isOpen={this.state.isOpen}
          onAfterOpen={this.onAfterOpenModal}
        >
          <div className="DiscountModal__Title">
            <span>{this.props.t("Enter your discount coupon")}</span>
            <button className="DiscountModal__Title__CloseButton" type="button" onClick={this.onModalCancel}>
              <i className="fa fa-times" />
            </button>
          </div>
          <div className="DiscountModal__Content" ref={this.modalContentRef}>
            <div className="" style={{ minHeight: 60 }}>
              <SchemaField
                name={this.props.name}
                label={false}
                schema={this.props.schema}
                showError={!this.props.isChecking}
              />
            </div>
            <div className="" style={{ minHeight: 50, paddingBottom: 20 }}>
              {this.props.isChecking && <Loader message={this.props.t("Checking...")} spinnerSize={16} />}
            </div>

            {this.props.isValid && !this.props.isChecking && (
              <div className="alert alert-success">{this.props.t("Valid code!")}</div>
            )}

            <div className="d-flex justify-content-between">
              <button onClick={this.onModalCancel} type="button" className="btn btn-default">
                {this.props.t(this.props.discountCode && this.props.isValid ? "Clear" : "Cancel")}
              </button>
              {this.props.isValid && (
                <button
                  onClick={this.onModalAccept}
                  type="button"
                  className="btn btn-primary"
                  disabled={!this.props.isValid}
                >
                  {this.props.t("Accept")}
                </button>
              )}
            </div>
          </div>
        </ReactModal>
      </div>
    );
  }
}

const valueSelector = formValueSelector("cart");

const selectFieldError = createSelector(
  [(state, field) => field, state => state.form && state.form.cart],
  (field, state) => {
    let err = getIn(state, `syncErrors.${field}`);
    if (err) {
      return err;
    }
    err = getIn(state, `asyncErrors.${field}`);
    if (err) {
      return err;
    }
    return null;
  }
);

const selectValidCode = createSelector(
  [state => state.cart],
  cart => cart.getIn(["context", "discounts", "validCode"], false)
);

export default translate()(
  connect(
    state => ({
      isChecking: state.cart.get("isFetchingContext", false),
      discountCode: valueSelector(state, "checkout.payment.discountCode"),
      isValid: selectValidCode(state),
      fieldError: selectFieldError(state, "checkout.payment.discountCode")
    }),
    {
      formBlur: actions.blur,
      formChange: actions.change,
      formClearAsyncError: actions.clearAsyncError,
      formClearSyncError
    }
  )(DiscountCodeField)
);
